/** Menu and gameplay keybinds. Stored in localStorage as lowercase keys. */

export type KeybindAction = "return" | "pause" | "menu" | "playAgain" | "nextLevel" | "quickRestart" | "uiCursor";

export const KEYBIND_ACTIONS: KeybindAction[] = ["return", "pause", "menu", "playAgain", "nextLevel", "quickRestart", "uiCursor"];

/** Optional actions default to unassigned (empty string). */
export const KEYBIND_OPTIONAL: KeybindAction[] = ["playAgain", "nextLevel"];

/** Triple-tap actions may share keys with other binds; conflicts are ignored. */
export const KEYBIND_IGNORE_CONFLICTS: KeybindAction[] = ["quickRestart"];

export const KEYBIND_DEFAULTS: Record<KeybindAction, string> = {
    return: "h",
    pause: "p",
    menu: "e",
    playAgain: "",
    nextLevel: "",
    quickRestart: " ",
    uiCursor: "v",
};

export const KEYBIND_STORAGE_KEYS: Record<KeybindAction, string> = {
    return: "keybindReturn",
    pause: "keybindPause",
    menu: "keybindMenu",
    playAgain: "keybindPlayAgain",
    nextLevel: "keybindNextLevel",
    quickRestart: "keybindQuickRestart",
    uiCursor: "keybindUiCursor",
};

const KEY_DISPLAY: Record<string, string> = {
    " ": "Space",
    arrowup: "↑",
    arrowdown: "↓",
    arrowleft: "←",
    arrowright: "→",
    escape: "Esc",
    enter: "Enter",
    tab: "Tab",
    backspace: "Backspace",
};

function normalizeStoredKey(key: string): string {
    if (key === " ") return " ";
    return String(key).toLowerCase();
}

function isOptionalAction(action: KeybindAction): boolean {
    return KEYBIND_OPTIONAL.includes(action);
}

function readStoredKey(action: KeybindAction): string {
    const raw = localStorage.getItem(KEYBIND_STORAGE_KEYS[action]);
    if (isOptionalAction(action)) {
        if (raw == null || raw === "") return "";
        return normalizeStoredKey(raw);
    }
    if (raw == null || raw === "") return KEYBIND_DEFAULTS[action];
    return normalizeStoredKey(raw);
}

export function getKeybindKey(action: KeybindAction): string {
    return readStoredKey(action);
}

export function hasKeybindAssigned(action: KeybindAction): boolean {
    return getKeybindKey(action) !== "";
}

export function setKeybindKey(action: KeybindAction, key: string) {
    localStorage.setItem(KEYBIND_STORAGE_KEYS[action], normalizeStoredKey(key));
}

export function resetKeybindKey(action: KeybindAction) {
    localStorage.removeItem(KEYBIND_STORAGE_KEYS[action]);
}

export function formatKeybindDisplay(key: string): string {
    if (!key) return "None";
    const norm = normalizeStoredKey(key);
    if (!norm) return "None";
    if (norm.length === 1 && norm !== " ") return norm.toUpperCase();
    if (KEY_DISPLAY[norm]) return KEY_DISPLAY[norm];
    if (norm.startsWith("arrow")) return norm.slice(5).charAt(0).toUpperCase() + norm.slice(6);
    return norm.charAt(0).toUpperCase() + norm.slice(1);
}

export function keybindBracket(action: KeybindAction): string {
    const key = getKeybindKey(action);
    if (!key) return "";
    return `[${formatKeybindDisplay(key)}]`;
}

const IGNORED_CAPTURE_KEYS = new Set(["Shift", "Control", "Alt", "Meta", "CapsLock", "NumLock", "ScrollLock"]);

/** Map a keydown to a storable key, or null if not assignable. */
export function keyboardEventToStoredKey(e: KeyboardEvent): string | null {
    if (e.ctrlKey || e.metaKey || e.altKey) return null;
    const key = e.key;
    if (!key || IGNORED_CAPTURE_KEYS.has(key)) return null;
    if (key.length === 1) return key.toLowerCase();
    const lower = key.toLowerCase();
    if (lower.startsWith("arrow") || lower === "enter" || lower === "escape" || lower === "tab" || lower === "backspace") {
        return lower;
    }
    if (key === " ") return " ";
    return null;
}

export function keyEventMatchesAction(e: KeyboardEvent, action: KeybindAction): boolean {
    const stored = getKeybindKey(action);
    if (!stored) return false;
    const captured = keyboardEventToStoredKey(e);
    if (captured == null) {
        return normalizeStoredKey(String(e.key || "")) === stored;
    }
    return captured === stored;
}

export function findKeybindConflict(action: KeybindAction, key: string): KeybindAction | null {
    if (KEYBIND_IGNORE_CONFLICTS.includes(action)) return null;
    const norm = normalizeStoredKey(key);
    if (!norm) return null;
    for (const a of KEYBIND_ACTIONS) {
        if (a === action || KEYBIND_IGNORE_CONFLICTS.includes(a)) continue;
        if (getKeybindKey(a) === norm) return a;
    }
    return null;
}

export function replaceKeybindPlaceholders(text: string): string {
    return text
        .replace(/\[H\]/g, keybindBracket("return"))
        .replace(/\[P\]/g, keybindBracket("pause"))
        .replace(/\[E\]/g, keybindBracket("menu"));
}
